import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, MessageCircle, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3";

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/matches").then((r) => setMatches(r.data)).finally(() => setLoading(false));
  }, []);

  const unmatch = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Unmatch ${m.user.name}? This removes the match and your chat.`)) return;
    try {
      await api.post(`/unmatch/${m.match_id}`);
      setMatches((prev) => prev.filter((x) => x.match_id !== m.match_id));
      toast.success("Unmatched");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-3xl mx-auto">
      <div className="overline mb-1">Mutual desire</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-8">Your Matches</h2>

      {loading ? (
        <div className="text-[#A0A0A0]">Loading…</div>
      ) : matches.length === 0 ? (
        <div className="pe-card rounded-3xl p-10 text-center">
          <Heart className="text-[#D4AF37] mx-auto mb-4" size={40} />
          <h3 className="font-heading text-2xl mb-2">No matches yet</h3>
          <p className="text-sm text-[#A0A0A0] mb-6">Keep swiping in Discover — when the feeling's mutual, they'll appear here.</p>
          <button data-testid="go-discover" onClick={() => navigate("/discover")} className="btn-gold px-6 py-2 rounded-full text-sm">Start swiping</button>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((m, i) => (
            <motion.div key={m.match_id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`match-${m.match_id}`}
              onClick={() => navigate(`/chat/${m.match_id}`)}
              className="pe-card rounded-2xl p-4 flex items-center gap-4 cursor-pointer">
              <img src={m.user.photos?.[0] || FALLBACK} alt={m.user.name}
                className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/40" />
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xl text-white">{m.user.name}{m.user.age ? `, ${m.user.age}` : ""}</div>
                <div className="text-sm text-[#A0A0A0] truncate">{m.last_message || "Say something enticing…"}</div>
              </div>
              <MessageCircle className="text-[#D4AF37]" size={22} />
              <button data-testid={`unmatch-${m.match_id}`} onClick={(e) => unmatch(e, m)} title="Unmatch"
                className="text-[#A0A0A0] hover:text-[#E63946] p-1 transition-colors"><X size={18} /></button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
